import React from 'react';
import { useSubscription } from '@apollo/client';

import { client } from '@oysterjs/ais/api/client';
import {
  type CaraWorkspaceFieldsFragment,
  WatchCaraWorkspaceDocument,
  type WatchCaraWorkspaceSubscription,
  type WatchCaraWorkspaceSubscriptionVariables
} from '@oysterjs/ais/api/graphql';

/**
 * Subscribes to live workspace updates and applies them to local state. Reconnects on
 * error using exponential backoff (capped at 10s) and on completion after a 1s delay.
 *
 * Subscription emissions are coalesced through `requestAnimationFrame` so that a
 * burst of token-level updates from the backend produces at most one React commit
 * per display frame. Without this, a long streaming response sees the conversation
 * tree reconciled dozens of times per second and renders fall progressively further
 * behind the wire, manifesting as the UI lagging the backend by tens of seconds on
 * larger workspaces. Mutation responses (send/stop) still call the returned
 * `setWorkspace` directly and bypass the rAF buffer.
 */
export function useCaraWorkspaceSubscription(initial: CaraWorkspaceFieldsFragment) {
  const [workspace, setWorkspace] = React.useState<CaraWorkspaceFieldsFragment>(initial);

  const { data, error, restart } = useSubscription<
    WatchCaraWorkspaceSubscription,
    WatchCaraWorkspaceSubscriptionVariables
  >(WatchCaraWorkspaceDocument, {
    variables: { id: workspace.id },
    client: client,
    onComplete: () => setTimeout(() => restart(), 1000)
  });

  const [, setErrorCount] = React.useState(0);

  // Latest subscription payload that hasn't been flushed into React state yet,
  // plus the in-flight rAF handle. Both stay in refs so we don't trigger extra
  // renders while buffering, and so multiple emissions within a single frame
  // collapse into a single commit using the freshest data.
  const pendingWorkspaceRef = React.useRef<CaraWorkspaceFieldsFragment | null>(null);
  const flushHandleRef = React.useRef<number | null>(null);

  React.useEffect(
    () => () => {
      if (flushHandleRef.current != null) {
        cancelAnimationFrame(flushHandleRef.current);
        flushHandleRef.current = null;
      }
      pendingWorkspaceRef.current = null;
    },
    []
  );

  React.useEffect(() => {
    if (data?.watchCaraWorkspace) {
      pendingWorkspaceRef.current = data.watchCaraWorkspace;
      if (flushHandleRef.current == null) {
        flushHandleRef.current = requestAnimationFrame(() => {
          flushHandleRef.current = null;
          const next = pendingWorkspaceRef.current;
          pendingWorkspaceRef.current = null;
          if (next) {
            setWorkspace((current) => (shouldApplyWorkspaceUpdate(current, next) ? next : current));
          }
        });
      }
    }

    setErrorCount((prev) => {
      let next = 0;
      if (error) {
        next = prev + 1;
        setTimeout(() => restart(), Math.min(Math.pow(2, next) * 1000, 10000));
      }

      return next;
    });
  }, [data?.watchCaraWorkspace, error, restart]);

  return { workspace, setWorkspace };
}

export function shouldApplyWorkspaceUpdate(
  current: Pick<CaraWorkspaceFieldsFragment, 'updatedAt'>,
  incoming: Pick<CaraWorkspaceFieldsFragment, 'updatedAt'>
) {
  const currentUpdatedAt = new Date(current.updatedAt).getTime();
  const incomingUpdatedAt = new Date(incoming.updatedAt).getTime();

  if (!Number.isFinite(currentUpdatedAt) || !Number.isFinite(incomingUpdatedAt)) {
    return true;
  }

  return incomingUpdatedAt >= currentUpdatedAt;
}
